import { useState, useCallback, useMemo } from 'react';

/**
 * Custom hook to manage row selection in tables
 *
 * @param {Array} items - Array of table items
 * @param {String} idField - Name of the field containing the unique ID of each item
 * @returns {Object} Object with selection functions and state
 */
const useTableSelection = (items = [], idField = 'id') => {
  // State to store the IDs of selected items
  const [selectedIds, setSelectedIds] = useState([]);

  // Check if all items are selected
  const isAllSelected = useMemo(() => {
    return items.length > 0 && selectedIds.length === items.length;
  }, [items, selectedIds]);

  // Check if some items are selected (for indeterminate checkbox state)
  const isSomeSelected = useMemo(() => {
    return selectedIds.length > 0 && selectedIds.length < items.length;
  }, [items, selectedIds]);

  // Check if a specific item is selected
  const isSelected = useCallback(
    id => {
      return selectedIds.includes(id);
    },
    [selectedIds]
  );

  // Select or deselect a specific item
  const toggleSelection = useCallback(id => {
    setSelectedIds(prevSelectedIds => {
      if (prevSelectedIds.includes(id)) {
        return prevSelectedIds.filter(selectedId => selectedId !== id);
      } else {
        return [...prevSelectedIds, id];
      }
    });
  }, []);

  // Select or deselect all items
  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      const allIds = items.map(item => item[idField]);
      setSelectedIds(allIds);
    }
  }, [isAllSelected, items, idField]);

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  // Return selected items
  const selectedItems = useMemo(() => {
    return items.filter(item => selectedIds.includes(item[idField]));
  }, [items, selectedIds, idField]);

  return {
    selectedIds,
    selectedItems,
    isAllSelected,
    isSomeSelected,
    isSelected,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
  };
};

export default useTableSelection;
